import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { generarPDFReceta } from '../../utils/pdfGenerator';
import doctorService from '../../api/doctorService';

const RecetaMedica = ({ 
  paciente, // Datos del paciente
  proximaCita = '', // Fecha de próxima cita (opcional)
  diagnostico = '', // Diagnóstico (opcional)
  doctorNombre = '', // Nombre del doctor (opcional)
  onCancel = () => {}, // Función para cancelar
}) => {
  const [cargando, setCargando] = useState(false);
  const [medicamentos, setMedicamentos] = useState([{ 
    nombre: '', 
    dosis: '', 
    frecuencia: '',
    duracion: '',
    indicaciones: '' 
  }]);
  const [indicacionesGenerales, setIndicacionesGenerales] = useState('');
  const [indicacionesEspecificas] = useState('');
  const [configuracionReceta, setConfiguracionReceta] = useState(null);
  
  // Estados para la próxima cita
  const [fechaProximaCita] = useState(null);
  const [horaProximaCita] = useState('');
  const [] = useState(false);
  const [] = useState(false);

  // Cargar configuración de receta al montar el componente
  useEffect(() => {
    const cargarConfiguracion = async () => {
      try {
        const config = await doctorService.getConfiguracionReceta();
        if (config && config.data) {
          setConfiguracionReceta(config.data);
          console.log('Configuración de receta cargada:', config.data);
        }
      } catch (error) {
        console.error('Error al cargar configuración de receta:', error);
      }
    };

    cargarConfiguracion();
  }, []);

  // Manejar cambios en los medicamentos
  const handleMedicamentoChange = (index, campo, valor) => {
    const nuevosMedicamentos = [...medicamentos];
    nuevosMedicamentos[index] = {
      ...nuevosMedicamentos[index],
      [campo]: valor
    };
    setMedicamentos(nuevosMedicamentos);
  };

  // Agregar un nuevo medicamento
  const agregarMedicamento = () => {
    setMedicamentos([
      ...medicamentos, 
      { nombre: '', dosis: '', frecuencia: '', indicaciones: '' }
    ]);
  };

  // Eliminar un medicamento
  const eliminarMedicamento = (index) => {
    if (medicamentos.length === 1) {
      // No eliminar si solo queda uno
      toast.error('Debe haber al menos un medicamento en la receta');
      return;
    }
    
    const nuevosMedicamentos = medicamentos.filter((_, i) => i !== index);
    setMedicamentos(nuevosMedicamentos);
  };

  // Generar PDF de receta
  const generarReceta = async () => {
    // Validar que al menos un medicamento tenga nombre
    if (!medicamentos.some(med => med.nombre.trim())) {
      toast.error('Debe agregar al menos un medicamento con nombre');
      return;
    }

    try {
      setCargando(true);

      // Agregar indicaciones generales y específicas al primer medicamento
      const medicamentosConIndicaciones = [...medicamentos];
      if (indicacionesGenerales) {
        medicamentosConIndicaciones[0] = {
          ...medicamentosConIndicaciones[0],
          indicacionesGenerales
        };
      }
      
      if (indicacionesEspecificas) {
        medicamentosConIndicaciones[0] = {
          ...medicamentosConIndicaciones[0],
          indicacionesEspecificas
        };
      }

      // Ruta de la plantilla PDF si existe en la configuración
      const rutaPlantilla = configuracionReceta?.rutaPlantillaPDF || null;
      console.log('Usando plantilla de receta:', rutaPlantilla);

      // Formatear la próxima cita si existe
      let proximaCitaFormateada = '';
      if (fechaProximaCita && horaProximaCita) {
        proximaCitaFormateada = `${formatDateForDisplay(fechaProximaCita)} a las ${horaProximaCita}`;
      }
      
      // Generar PDF con los datos
      const resultado = await generarPDFReceta(
        medicamentosConIndicaciones, 
        paciente, 
        diagnostico, 
        proximaCitaFormateada || proximaCita,
        doctorNombre,
        rutaPlantilla
      );

      if (resultado) {
        toast.success('Receta generada y descargada exitosamente');
        // Opcional: cerrar modal/formulario después de generar
        onCancel();
      } else {
        toast.error('Error al generar la receta');
      }
    } catch (error) {
      console.error('Error al generar receta:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setCargando(false);
    }
  };

  // Estilos
  const inputStyle = {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginBottom: '8px'
  };

  const buttonStyle = {
    padding: '8px 16px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  };

  const deleteButtonStyle = {
    padding: '6px 12px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  };

  const addButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px'
  };


  // Formatear fecha para mostrar
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
        Generar Receta Médica
      </h2>

      {/* Información del paciente */}
      <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '8px' }}>Datos del Paciente</h3>
        <p><strong>Nombre:</strong> {paciente ? `${paciente.nombre} ${paciente.apellido}` : 'No especificado'}</p>
        {paciente?.cedula && <p><strong>Cédula:</strong> {paciente.cedula}</p>}
      </div>

      {/* Diagnóstico */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '8px' }}>Diagnóstico</h3>
        <input
          type="text"
          value={typeof diagnostico === 'string' ? diagnostico : diagnostico?.descripcion || ''}
          onChange={(e) => diagnostico = e.target.value}
          placeholder="Escriba el diagnóstico aquí"
          style={inputStyle}
        />
      </div>

      {/* Medicamentos */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>Medicamentos</h3>
          <button
            type="button"
            onClick={agregarMedicamento}
            style={addButtonStyle}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Agregar Medicamento
          </button>
        </div>

        {medicamentos.map((medicamento, index) => (
          <div key={index} style={{ 
            border: '1px solid #ddd', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '12px',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: '600', margin: 0 }}>Medicamento #{index + 1}</h4>
              <button
                type="button"
                onClick={() => eliminarMedicamento(index)}
                style={deleteButtonStyle}
              >
                Eliminar
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px', fontWeight: '500' }}>
                  Nombre
                </label>
                <input
                  type="text"
                  value={medicamento.nombre}
                  onChange={(e) => handleMedicamentoChange(index, 'nombre', e.target.value)}
                  placeholder="Nombre del medicamento"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px', fontWeight: '500' }}>
                  Dosis
                </label>
                <input
                  type="text"
                  value={medicamento.dosis}
                  onChange={(e) => handleMedicamentoChange(index, 'dosis', e.target.value)}
                  placeholder="Ej: 500mg"
                  style={inputStyle}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px', fontWeight: '500' }}>
                  Frecuencia
                </label>
                <input
                  type="text"
                  value={medicamento.frecuencia}
                  onChange={(e) => handleMedicamentoChange(index, 'frecuencia', e.target.value)}
                  placeholder="Ej: Cada 8 horas"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px', fontWeight: '500' }}>
                  Duración
                </label>
                <input
                  type="text"
                  value={medicamento.duracion}
                  onChange={(e) => handleMedicamentoChange(index, 'duracion', e.target.value)}
                  placeholder="Ej: 7 días"
                  style={inputStyle}
                />
              </div>
            </div>



            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px', fontWeight: '500' }}>
                Indicaciones específicas
              </label>
              <textarea
                value={medicamento.indicaciones}
                onChange={(e) => handleMedicamentoChange(index, 'indicaciones', e.target.value)}
                placeholder="Indicaciones específicas para este medicamento"
                rows="2"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Indicaciones generales */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '1rem', fontWeight: '600', marginBottom: '8px' }}>
          Indicaciones Generales
        </label>
        <textarea
          value={indicacionesGenerales}
          onChange={(e) => setIndicacionesGenerales(e.target.value)}
          placeholder="Indicaciones generales para toda la receta"
          rows="4"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>


      {/* Información de la plantilla */}
      {configuracionReceta?.rutaPlantillaPDF ? (
        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #86efac' }}>
          <p style={{ fontSize: '0.9rem', color: '#10b981', margin: 0 }}>
            <strong>Plantilla PDF:</strong> Se utilizará la plantilla configurada ({configuracionReceta.rutaPlantillaPDF.split('/').pop()})
          </p>
        </div>
      ) : (
        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fff7ed', borderRadius: '6px', border: '1px solid #fdba74' }}>
          <p style={{ fontSize: '0.9rem', color: '#f97316', margin: 0 }}>
            <strong>Aviso:</strong> No hay ninguna plantilla PDF configurada. Se generará una receta con formato básico.
          </p>
          <p style={{ fontSize: '0.9rem', color: '#f97316', margin: '4px 0 0 0' }}>
            Puede configurar una plantilla en la sección Configuración {'>'} Impresión.
          </p>
        </div>
      )}

      {/* Botones */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ ...buttonStyle, backgroundColor: '#9ca3af' }}
          disabled={cargando}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={generarReceta}
          disabled={cargando}
          style={buttonStyle}
        >
          {cargando ? (
            <>
              <svg style={{ 
                animation: 'spin 1s linear infinite',
                marginRight: '8px',
                display: 'inline-block',
                verticalAlign: 'middle'
              }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
              Generando...
            </>
          ) : 'Generar Receta'}
        </button>
      </div>
    </div>
  );
};

export default RecetaMedica;